import cloneDeep from 'lodash/cloneDeep';
import set from 'lodash/set';
import get from 'lodash/get';
import { mapState } from 'vuex';
import debounce from 'lodash/debounce';
import apiService from '@/services/apiService/dashboards';
import ColorsMixin from '@/mixins/colors.mixin';
export default {
  name: 'GaugeChartOptionsEditor',
  mixins: [ColorsMixin],
  props: {
    value: {
      type: Object,
      required: true
    },
    dashboardId: {
      type: String,
      required: true
    },
    customParameters: {
      type: Object,
      required: true
    }
  },
  data() {
    return {
      isFetchingAvailableStates: true,
      states: [],
      nextThresholdValue: null
    };
  },
  computed: {
    ...mapState('app', ['color', 'inpOutlined', 'inpFilled']),
    gaugeValueDeterminations() {
      const determinations = ['min', 'max', 'average', 'sum', 'earliest', 'latest'];
      return determinations.map(determination => ({
        text: this.$t(`Dashboards.components.widgetChartOptionsEditor.inputs.gaugeChart.valueDetermination.values.${determination}`),
        value: determination
      }));
    },
    showDetailValue: {
      get() {
        return get(this.value, 'chartOptions.gaugeChart.showDetailValue', true);
      },
      set(value) {
        this.changeByKey('chartOptions.gaugeChart.showDetailValue', value, 'boolean');
      }
    },
    detailValueFormats() {
      const formats = new Set();
      this.states.map(state => {
        const unit = get(state, 'properties.unit');
        if (typeof unit !== 'string') {
          return;
        }
        if (unit.trim() === '') {
          return;
        }
        formats.add(unit);
      });
      return [{
        value: null,
        text: this.$t('Dashboards.components.widgetChartOptionsEditor.inputs.gaugeChart.detailValueFormat.none')
      }, ...Array.from(formats).map(format => ({
        text: format,
        value: format
      }))];
    },
    detailValueFormat: {
      get() {
        const currentValue = get(this.value, 'chartOptions.gaugeChart.detailValueFormat', null);
        const matchingItem = this.detailValueFormats.find(item => item.value === currentValue);
        return matchingItem || {
          value: currentValue,
          text: currentValue
        };
      },
      set(value) {
        const actualValue = typeof value === 'object' ? value.value : value;
        this.changeByKey('chartOptions.gaugeChart.detailValueFormat', actualValue, 'string');
      }
    },
    showPointer: {
      get() {
        return get(this.value, 'chartOptions.gaugeChart.showPointer', true);
      },
      set(value) {
        this.changeByKey('chartOptions.gaugeChart.showPointer', value, 'boolean');
      }
    },
    showAxisLabels: {
      get() {
        return get(this.value, 'chartOptions.gaugeChart.showAxisLabels', true);
      },
      set(value) {
        this.changeByKey('chartOptions.gaugeChart.showAxisLabels', value, 'boolean');
      }
    },
    entityFilters() {
      return {
        twinFilter: get(this.value, 'twinFilter', {}),
        virtualDeviceFilter: get(this.value, 'virtualDeviceFilter', {})
      };
    }
  },
  watch: {
    entityFilters: {
      async handler() {
        this.fetchAvailableStates();
      },
      immediate: true
    }
  },
  methods: {
    nextThresholdValueRule(value) {
      if (value === '' || value === null || value === undefined) {
        return true;
      }
      if (isNaN(value)) {
        return this.$t('Dashboards.components.widgetChartOptionsEditor.inputs.gaugeChart.thresholds.error.notANumber');
      }
      const min = this.getByKey('chartOptions.gaugeChart.min', 0);
      if (value < min) {
        return this.$t('Dashboards.components.widgetChartOptionsEditor.inputs.gaugeChart.thresholds.error.belowMin');
      }
      const max = this.getByKey('chartOptions.gaugeChart.max', 100);
      if (value > max) {
        return this.$t('Dashboards.components.widgetChartOptionsEditor.inputs.gaugeChart.thresholds.error.aboveMax');
      }
      return true;
    },
    changeByKey(key, val, valueTransformTo, options = {}) {
      const clone = cloneDeep(this.value);
      if (valueTransformTo === 'number') {
        if (val === '') {
          val = undefined;
        } else if (Array.isArray(val)) {
          val = val.map(v => parseFloat(v));
        } else {
          val = parseFloat(val);
        }
      }
      if (Array.isArray(val) && options.sort) {
        val = [...val].sort((a, b) => {
          switch (valueTransformTo) {
            case 'number':
              return a - b;
            default:
              return a.localeCompare(b);
          }
        });
      }
      if (Array.isArray(val) && options.unique) {
        val = [...new Set(val)];
      }
      set(clone, key, val);
      this.$emit('input', clone);
    },
    getByKey(key, defaultValue) {
      return get(this.value, key, defaultValue);
    },
    addThreshold() {
      // validate the input
      const isValid = this.$refs.nextThresholdValue.validate();
      if (!isValid) {
        return;
      }
      if (!this.nextThresholdValue) {
        return;
      }
      if (isNaN(this.nextThresholdValue)) {
        return;
      }
      const clone = cloneDeep(this.value);
      const currentThresholds = get(clone, 'chartOptions.gaugeChart.thresholds', []);
      const colorIndex = currentThresholds.length % this.colors.length;
      const color = this.colors[colorIndex].value;
      currentThresholds.push({
        value: this.nextThresholdValue,
        color
      });
      const sortedThresholds = currentThresholds.sort((a, b) => a.value - b.value);
      set(clone, 'chartOptions.gaugeChart.thresholds', sortedThresholds);
      this.$emit('input', clone);
      this.nextThresholdValue = null;
      this.$refs.nextThresholdValue.$el.focus();
    },
    removeThreshold(index) {
      const clone = cloneDeep(this.value);
      const thresholds = get(clone, 'chartOptions.gaugeChart.thresholds', []);
      thresholds.splice(index, 1);
      set(clone, 'chartOptions.gaugeChart.thresholds', thresholds);
      this.$emit('input', clone);
    },
    fetchAvailableStates: debounce(function () {
      this.isFetchingAvailableStates = true;
      this.actuallyFetchAvailableStates();
    }, 500),
    async actuallyFetchAvailableStates() {
      try {
        this.isFetchingAvailableStates = true;
        const allStates = await apiService.fetchAvailableStatesForDashboardFilter(this.dashboardId, this.entityFilters.twinFilter, this.entityFilters.virtualDeviceFilter, this.customParameters);
        const stateIdentifiers = get(this.value, 'chartOptions.stateIdentifiers', []);
        this.states = allStates.filter(state => stateIdentifiers.includes(state.key));
      } catch (e) {
        this.$snack(this.$t('Dashboards.components.gaugeChartOptionsEditor.snack.error.couldNotFetchStates', {
          error: e.message
        }), 'error');
        console.error(e);
      } finally {
        this.isFetchingAvailableStates = false;
      }
    }
  }
};